import React, { useState, useEffect, useRef } from "react"
import cx from "classnames"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import debounce from "lodash/debounce"

import infoIcon from "@/public/icons/tooltip-red.svg"
import wishIcon from "@/public/icons/favorites-empty-annsacks.svg"
import filledWishIcon from "@/public/icons/favorites-filled-annsacks.svg"
import printIcon from "@/public/icons/print-annsacks.svg"
import shareIcon from "@/public/icons/share-annsacks.svg"
import { addDataLayer } from "../analytics"
import { MadetoOrder } from "./MadeToOrder"
import QuantitySelector from "@/components/cart/QuantitySelector"
import { addProduct, removeItems } from "@/store/features/favoritesSlice"
import SocialShare from "@/components/SocialShare/v1/SocialShare"
import {
  getShortenedUrlwithGeneral,
  isAuthenticated,
  removeFromCompareProduct,
} from "@/utils/helper"
import { showToast, selectGenericState } from "@/store/features/genericSlice"
import Button from "@/components/core/Button/Button"
import Checkbox from "@/components/core/Checkbox/Checkbox"
import { aemAxiosInstance } from "@/constants/api"
import { GLOBAL_CONFIGURATION_ENDPOINT } from "@/constants/index"
import ToolTip from "@/components/core/ToolTip/ToolTip"
import { useRouter } from "next/router"
import Input from "@/components/core/Input/Input"

const AddToCart = props => {
  const [loading, setLoading] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [isChecked, setIsChecked] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState({ show: false, message: "" })
  const [showWelcome, setShowWelcome] = useState(false)
  const productCategory = props.productCategory ?? ""
  const dispatch = useDispatch()
  const [productStock, setProductStock] = useState(0)
  const enableFav =
    props.isDiscontinued || props.isColorDiscontinued ? true : false
  const [enableNotify, setEnableNotify] = useState(false)
  const [showBuyBoxArea, setShowBuyBoxArea] = useState(true)
  const [stockAlert, setStockAlert] = useState({ show: false, mssg: "" })
  const [showSocialLink, setShowSocialLink] = useState(false)
  const brandName = props.general?.siteName?.toLowerCase() ?? ""
  let compareJsonPath = props?.general?.compareJsonPath ?? ""
  const [categoryData, setCategoryData] = useState([])
  const [showQuantitySelector, enableQuantity] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [activeTab, setActiveTab] = useState("contact")
  const madeToOrderStatus = "Made to Order"
  const router = useRouter()
  const shareImg = useRef(null)
  const { toaster = {} } = useSelector(selectGenericState)

  const {
    disableEcommerce,
    replaceAddToCart,
    replaceAddToCartCtaLabel,
    replaceAddToCartCtaLink,
    linkNewTab,
  } = props.authorData
  useEffect(() => {
    setProductStock(props.productOnStock)
  }, [props.productOnStock])

  useEffect(() => {
    aemAxiosInstance({
      url: GLOBAL_CONFIGURATION_ENDPOINT,
      params: {
        path: props?.general?.compareJsonPath,
      },
    })
      .then(res => {
        if (res?.data?.data) {
          setCategoryData(res?.data?.data)
        } else {
          setCategoryData([])
        }
      })
      .catch(err => {
        console.error("Failed to get compare attributes JSON file", err)
      })
  }, [props.general])

  useEffect(() => {
    const tempType = ["grout", "lighting", "samples", "accessories"]
    enableQuantity(tempType.some(el => el === props.templateType))
  }, [props.templateType])

  useEffect(() => {
    if (
      props.compareData.comparedProducts.find(
        prod => prod.compareSku === props.sku
      )
    )
      setIsChecked(true)
    else setIsChecked(false)
  }, [props.compareData.comparedProducts, props.sku])

  useEffect(() => {
    if (showWelcome) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
      document.body.classList.remove("modal-open")
    }
  }, [showWelcome])

  useEffect(() => {
    if (props.productIsRetail) {
      setShowBuyBoxArea(false)
    } else if (!props.price) {
      setShowBuyBoxArea(false)
    } else {
      setShowBuyBoxArea(true)
    }
  }, [props.productIsRetail, props.price])

  const handleChange = qty => {
    setStockAlert({ show: false, mssg: "" })
    if (props.backOrder === "Yes" || props.isParts) {
      setQuantity(qty)
      return
    }
    setLoading(true)
    const cartItem =
      props.cart.lineItems?.find(item => item.variant.sku === props.sku) ?? {}
    const cartQuantity = cartItem.quantity ?? 0
    if (qty && qty > 0) {
      setQuantity(qty)
      getStock(props.sku)
        .then(stock => {
          if (cartQuantity > 0 && stock <= cartQuantity) {
            setStockAlert({
              show: true,
              mssg: props.t("kf.pdp.label.noItemsToShip"),
            })
          } else if (stock >= qty + cartQuantity) {
            props.onQtyChange(qty)
          } else {
            setProductStock(stock)
            if (stock > 0) {
              setQuantity(stock - cartQuantity)
              props.onQtyChange(stock - cartQuantity)
              setStockAlert({
                show: true,
                mssg: props.t("kf.pdp.label.availableToShip", { qty: stock }),
              })
            }
          }
          setLoading(false)
        })
        .catch(() => setLoading(false))
    }
  }
  const isCategoryMatchWithCompareJSON = categoryData?.some(
    item => item?.category === productCategory
  )

  const handleCompare = e => {
    setIsChecked(!isChecked)
    if (e.target.checked) {
      if (props.compareData.comparedProducts.length < 4) {
        addDataLayer({
          title: props.staticTexts.addToCompare.toLowerCase(),
          productInfo: props?.analyticsProductInfo,
        })

        let variantFlag = false
        const allVariants = props.compareData.comparedProducts
          ?.map(compareProduct => [
            ...compareProduct.variants,
            compareProduct.masterVariant,
          ])
          ?.flat()
        const allVariantSkus = allVariants?.map(allVariantsItr => allVariantsItr.sku)
        if (allVariantSkus.includes(props.sku)) {
          variantFlag = true
        }
        if (!isCategoryMatchWithCompareJSON) {
          setErrorMessage(props.staticTexts.sameVariants)
          setTimeout(() => {
            setErrorMessage("")
          }, 2000)
          setIsChecked(false)
          removeFromCompareProduct(props.sku)
        } else if (
          props.compareData.category !== "" &&
          props.compareData.category !== productCategory
        ) {
          setErrorMessage(props.staticTexts.sameCategory)
          setTimeout(() => {
            setErrorMessage("")
          }, 2000)
          setIsChecked(false)
          props.onRemove(props.sku)
        } else if (variantFlag) {
          setErrorMessage(props.staticTexts.sameVariants)
          setTimeout(() => {
            setErrorMessage("")
          }, 2000)
          setIsChecked(false)
          props.onRemove(props.sku)
        } else {
          props.onAdd(props.sku)
        }
      } else {
        setErrorMessage(props.staticTexts.maxProduct)
        setTimeout(() => {
          setErrorMessage("")
        }, 2000)
        setIsChecked(false)
        props.onRemove(props.sku)
      }
    } else {
      addDataLayer({
        title: "remove compare",
        productInfo: props?.analyticsProductInfo,
      })
      props.onRemove(props.sku)
    }
  }

  const handleAtcDatalayer = params => {
    const headerAtcIsClicked =
      localStorage?.getItem?.("headerAtcIsClicked") || false
    addDataLayer({
      title: params?.title || props.staticTexts.addToCart.toLowerCase(),
      productInfo: props?.analyticsProductInfo,
      isSticky: headerAtcIsClicked,
      cartData: params?.data,
      url: params?.link,
      calculated_Values: JSON.stringify({
        squareFeetNeeded:
          Number(
            document.getElementById("txt_prod_price")?.getAttribute("value") ??
              ""
          ) || "n/a",
        overage:
          Number(
            document.querySelector("#trim-calculator__overage-value-selected")
              ?.innerText ?? ""
          ) ||
          Number(
            document
              .getElementById("trim-calculator__overage-input-box")
              ?.getAttribute("value") ?? ""
          ) ||
          "n/a",
        totalSquareFeet:
          Number(
            document
              .getElementById("trim-calculator__totalSquareFeet")
              ?.getAttribute("value") ?? ""
          ) || "n/a",
        totalCoverage:
          Number(
            document
              .getElementById("trim-calculator__totalCoverage")
              ?.getAttribute("value") ?? ""
          ) || "n/a",
        totalBoxes:
          Number(
            document
              .getElementById("trim-calculator__totalBoxes")
              ?.getAttribute("value") ?? ""
          ) || "n/a",
        totalPrice:
          Number(
            document
              .getElementById("trim-calculator__finalTotal")
              ?.getAttribute("price") ?? ""
          ) || "n/a",
      }),
    })
    headerAtcIsClicked && localStorage.removeItem("headerAtcIsClicked")
  }

  const handleAddToCart = () => {
    let addToCartAttr =
      document.querySelector(".add-to-cart__main-button") &&
      document.querySelector(".add-to-cart__main-button") !== null
        ? document
            .querySelector(".add-to-cart__main-button")
            .getAttribute("templatetype")
        : ""
    const cartItem =
      props.cart.lineItems?.find(item => item.variant.sku === props.sku) ?? {}
    const cartQuantity = cartItem.quantity ?? 0
    let additionalInfo = {}
    let customQuantity = 0
    let totalOverage = ""
    let totalArea = 0
    let totalNeeded = 0
    let customQuantityTmp = ""
    let soldByBox = ""
    let soldByUom = ""
    let totalSFNeeded = ""
    if (addToCartAttr === "trim" || addToCartAttr === "tile") {
      totalOverage = Number(
        document.querySelector("#trim-calculator__overage-value-selected")
          ? document.querySelector("#trim-calculator__overage-value-selected")
              ?.innerText
          : ""
      )
      totalOverage =
        document.querySelector("#trim-calculator__overage-input-box")?.value ??
        totalOverage
      if (document.querySelector(".trim-calculator_main-block")) {
        customQuantityTmp = Number(
          document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("totalPeices")
        )
        soldByUom = Number(
          document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("soldByUom")
        )
        soldByBox = Number(
          document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("soldByBox")
        )
        totalArea = Number(
          document
            .querySelector(".trim-calculator_main-block")
            .getAttribute("totalOverage")
        )
        totalNeeded = Number(
          document.querySelector(".trim-calculator__square-ft-value")
            ? document.querySelector(".trim-calculator__square-ft-value").value
            : ""
        )
        totalSFNeeded = Number(
          document
            .querySelector("#trim-calculator__totalSquareFeet")
            ?.getAttribute("value")
        )
      }
      if (customQuantityTmp && customQuantityTmp > 0) {
        customQuantity = customQuantityTmp
      }
      if ((totalArea !== "" && totalArea > 0) || totalOverage !== "") {
        additionalInfo = {
          roomInfo: [
            '{"productType":"' +
              addToCartAttr +
              '","totalSFNeeded":"' +
              totalSFNeeded +
              '","totalNeeded":"' +
              totalNeeded +
              '","totalUserNeeded":"' +
              totalNeeded +
              '","totalSFUserNeeded":"' +
              totalSFNeeded +
              '","totalArea":"' +
              totalArea.toFixed(2) +
              '","soldByBox":"' +
              soldByBox +
              '","soldByUom":"' +
              soldByUom +
              '"}',
          ],
          customQuantity: soldByBox
            ? Number(customQuantity * soldByBox)
            : customQuantity,
          overage: totalOverage.toString(),
        }
      }
    }
    setStockAlert({ show: false, mssg: "" })
    if (props.backOrder === "Yes" || props.isParts) {
      addToCartAttr === "trim" || addToCartAttr === "tile"
        ? props.onAddToCart(
            [
              {
                sku: props.sku,
                quantity: quantity,
                additionalInfo: additionalInfo,
                isBundle: props.isComboSku,
                brand: brandName,
              },
            ],
            handleAtcDatalayer
          )
        : cartItem?.id
        ? props.updateRecalculateRoomInfo(
            cartItem?.id,
            { quantity: quantity + cartQuantity },
            quantity,
            handleAtcDatalayer
          )
        : props.onAddToCart(
            [
              {
                sku: props.sku,
                quantity: quantity,
                isBundle: props.isComboSku,
                brand: brandName,
                additionalInfo: { customQuantity: quantity },
              },
            ],
            handleAtcDatalayer
          )
      return
    }

    getStock(props.sku).then(stock => {
      setProductStock(stock)
      let totalQuantity = 0
      totalQuantity =
        addToCartAttr === "trim" || addToCartAttr === "tile"
          ? cartQuantity + customQuantity
          : cartQuantity + quantity
      if (totalQuantity > stock) {
        if (stock > cartQuantity) {
          const totalPiecesNo = soldByBox ? Number(stock * soldByBox) : stock
          const totalCoverage =
            Number(soldByUom) > 0 && totalPiecesNo > 0
              ? totalPiecesNo / Number(soldByUom)
              : 0
          const overagePercent =
            totalCoverage > 0 && totalOverage > 0
              ? (Math.ceil(totalCoverage) * totalOverage) / 100
              : 0
          const neededArea = Math.ceil(totalCoverage) - overagePercent
          if (cartItem?.id) {
            if (addToCartAttr === "trim" || addToCartAttr === "tile") {
              //For tile and trim if already added to cart and added amount exceeds stock
              const roomInfo = cartItem?.custom?.fields?.roomInfo[0] || ""
              const parsedRoomInfo = roomInfo ? JSON.parse(roomInfo) : ""
              const totalSFNeededFinal = parsedRoomInfo?.totalSFNeeded
                ? Number(parsedRoomInfo?.totalSFNeeded) + Number(totalSFNeeded)
                : Number(totalSFNeeded)
              const updatedSf =
                (totalSFNeededFinal * 100) / (100 + totalOverage)
              props.updateRecalculateRoomInfo(
                cartItem?.id,
                {
                  productType: addToCartAttr,
                  totalSFNeeded: totalSFNeededFinal,
                  totalNeeded: Math.floor(updatedSf * 100) / 100,
                  totalArea: totalCoverage.toFixed(2),
                  soldByBox: Number(soldByBox),
                  soldByUom: Number(soldByUom),
                  customQuantity: stock,
                  overage: totalOverage.toString(),
                  roomInfo: true,
                },
                stock,
                handleAtcDatalayer
              )
            } else {
              //For non tile and trim if already added to cart and added amount exceeds stock
              props.updateRecalculateRoomInfo(
                cartItem?.id,
                { quantity: stock },
                stock,
                handleAtcDatalayer
              )
            }
          } else {
            if (addToCartAttr === "trim" || addToCartAttr === "tile") {
              //For tile and trim if not already added to cart and added amount exceeds stock
              props.onAddToCart(
                [
                  {
                    sku: props.sku,
                    quantity: stock - cartQuantity,
                    additionalInfo: {
                      roomInfo: [
                        '{"productType":"' +
                          addToCartAttr +
                          '","totalSFNeeded":"' +
                          totalSFNeeded +
                          '","totalNeeded":"' +
                          totalNeeded +
                          '","totalUserNeeded":"' +
                          totalNeeded +
                          '","totalSFUserNeeded":"' +
                          totalSFNeeded +
                          '","totalArea":"' +
                          totalCoverage.toFixed(2) +
                          '","soldByBox":"' +
                          soldByBox +
                          '","soldByUom":"' +
                          soldByUom +
                          '"}',
                      ],
                      customQuantity: stock,
                      overage: totalOverage.toString(),
                    },
                    isBundle: props.isComboSku,
                    brand: brandName,
                  },
                ],
                handleAtcDatalayer
              )
            } else {
              //For non tile and trim if not already added to cart and added amount exceeds stock
              props.onAddToCart(
                [
                  {
                    sku: props.sku,
                    quantity: stock - cartQuantity,
                    isBundle: props.isComboSku,
                    brand: brandName,
                    additionalInfo: { customQuantity: stock },
                  },
                ],
                handleAtcDatalayer
              )
            }
          }
          setStockAlert({
            show: true,
            mssg: props.t("kf.pdp.label.availableToShip", { qty: stock }),
          })
        } else {
          setStockAlert({
            show: true,
            mssg: props.t("kf.pdp.label.noItemsToShip"),
          })
        }
      } else {
        if (cartItem?.id) {
          if (addToCartAttr === "trim" || addToCartAttr === "tile") {
            const totalPiecesNo = soldByBox
              ? Number(totalQuantity * soldByBox)
              : totalQuantity
            const totalCoverage =
              Number(soldByUom) > 0 && totalPiecesNo > 0
                ? totalPiecesNo / Number(soldByUom)
                : 0
            const overagePercent =
              totalCoverage > 0 && totalOverage > 0
                ? (Math.ceil(totalCoverage) * totalOverage) / 100
                : 0
            const neededArea = Math.ceil(totalCoverage) - overagePercent
            //For tile and trim if already added to cart and amount in stock
            const roomInfo = cartItem?.custom?.fields?.roomInfo[0] || ""
            const parsedRoomInfo = roomInfo ? JSON.parse(roomInfo) : ""
            const totalSFNeededFinal = parsedRoomInfo?.totalSFNeeded
              ? Number(parsedRoomInfo?.totalSFNeeded) + Number(totalSFNeeded)
              : Number(totalSFNeeded)
            const updatedSf = (totalSFNeededFinal * 100) / (100 + totalOverage)
            props.updateRecalculateRoomInfo(
              cartItem?.id,
              {
                productType: addToCartAttr,
                totalSFNeeded: totalSFNeededFinal,
                totalNeeded: Math.floor(updatedSf * 100) / 100,
                totalArea: totalCoverage.toFixed(2),
                soldByBox: Number(soldByBox),
                soldByUom: Number(soldByUom),
                customQuantity: totalQuantity,
                overage: totalOverage.toString(),
                roomInfo: true,
              },
              customQuantity,
              handleAtcDatalayer
            )
          } else {
            //For non tile and trim if already added to cart and added amount in stock
            props.updateRecalculateRoomInfo(
              cartItem?.id,
              { quantity: quantity + cartQuantity },
              quantity,
              handleAtcDatalayer
            )
          }
        } else {
          if (addToCartAttr === "trim" || addToCartAttr === "tile") {
            //For tile and trim if not already added to cart and added amount in stock
            props.onAddToCart(
              [
                {
                  sku: props.sku,
                  quantity: customQuantity,
                  isBundle: props.isComboSku,
                  additionalInfo: additionalInfo,
                  brand: brandName,
                },
              ],
              handleAtcDatalayer
            )
          } else {
            //For non tile and trim if not already added to cart and added amount in stock
            props.onAddToCart(
              [
                {
                  sku: props.sku,
                  quantity: quantity,
                  isBundle: props.isComboSku,
                  brand: brandName,
                  additionalInfo: { customQuantity: quantity },
                },
              ],
              handleAtcDatalayer
            )
          }
        }
      }
    })
  }

  const handleNotification = () => {
    addDataLayer({
      title: props.staticTexts.notifyMailCTA.toLowerCase(),
      productInfo: props?.analyticsProductInfo,
    })
    if (email) {
      let body = {
        customer: {
          id: "",
          firstName: "",
          lastName: "",
          email: email,
        },
        sku: props.sku,
        pdpURL: window.location.href,
      }
      apim
        .post("catalog/product/oos/notify", body)
        .then(res => {
          if (res) {
            setShowWelcome(true)
            setEmail("")
          }
        })
        .catch(() => {
          setEmailError({
            show: true,
            message: props.staticTexts.outOfStockError,
          })
        })
    }
  }

  const favoriteDatalayer = (status, msg, isAdd) => {
    addDataLayer({
      title: isAdd ? props.staticTexts.favTxt.toLowerCase() : "favorite remove",
      productInfo: props?.analyticsProductInfo,
      eventMsg: msg,
      eventStatus: status,
    })
  }

  const handleFavorite = e => {
    e.preventDefault()
    if (props?.isFavorite) {
      props?.favItem?.lineItems?.map(fav => {
        if (fav?.variant?.sku === props?.sku) {
          isAuthenticated(() =>
            dispatch(removeItems({ ids: [fav?.id], isDataLayerNeeded: false }))
              .unwrap()
              .then(() => {
                favoriteDatalayer(
                  "success",
                  props?.staticTexts?.removeItem,
                  false
                )
                dispatch(
                  showToast({
                    message:
                      getShortenedUrlwithGeneral(
                        toaster?.content?.unfavoriteSuccessMessage,
                        props?.general
                      ) || texts?.removeItem,
                    isVisible: true,
                  })
                )
              })
              .catch(err => {
                dispatch(
                  showToast({
                    message: props?.staticTexts?.removeItemError,
                    isVisible: true,
                  })
                )
                favoriteDatalayer(
                  "failure",
                  props?.staticTexts?.removeItemError,
                  false
                )
              })
          )
        }
      })
    } else {
      isAuthenticated(() =>
        dispatch(addProduct(props?.sku))
          .unwrap()
          .then(() => {
            dispatch(
              showToast({
                message:
                  getShortenedUrlwithGeneral(
                    toaster?.content?.successMessage,
                    props?.general
                  ) || props?.staticTexts?.favSuccessMsg,
                isVisible: true,
              })
            )
          })
          .catch(err => {
            dispatch(
              showToast({
                message: props?.staticTexts?.favErrorMsg,
                isVisible: true,
              })
            )
            favoriteDatalayer("failure", props?.staticTexts?.favErrorMsg, true)
          })
      )
    }
    document.body.classList.remove("modal-open")
    window.keyFocusCallBack = e.target.id
  }

  const handleEmailChange = val => {
    let inputValue = val.trim()
    setEmailError({ show: false, message: "" })
    setEmail(inputValue)
    if (!/\S+@\S+\.\S+/.test(inputValue)) setEnableNotify(false)
    else setEnableNotify(true)
  }
  const printPDP = () => {
    addDataLayer({
      title: props.staticTexts.print.toLowerCase(),
      productInfo: props?.analyticsProductInfo,
    })
    let printNoteLen = document.getElementsByClassName(
      "product-details__print-note"
    ).length
    if (printNoteLen === 0) {
      const printNote = `<div class='product-details__print-note'><div class='container'><div class='row'><div class='col-12'><h6>${props.staticTexts.priceFootnoteTitle}</h6><p>${props.staticTexts.priceFootnote}</p><h6>${props.staticTexts.colorFootnoteTitle}</h6> <p>${props.staticTexts.colorFootnote}</p></div></div></div></div>`
      let printNoteDiv = document.createElement("div")
      printNoteDiv.innerHTML = printNote
      document.body.appendChild(printNoteDiv)
    }
    window.print()
  }
  const productAnalyticsData = props.productData || {}

  const handleMadetoOrder = () => {
    setShowModal(true)
    setActiveTab("contact")
  }
  const closeModal = () => {
    setShowModal(false)
  }

  return (
    <div className="add-to-cart col-12">
      {/* { props.enablePromo && props.promoTitle && <div className="pdp-promo-wrap">
                <div tabIndex="0" role="button" onClick={showPromoDetails} className={cx({"promoTitle": true, "show": showPromo})}>
                    <div  dangerouslySetInnerHTML={{ __html: props.promoTitle}} />
                    {props.promoDescription && <img src={DownArrow} alt="" />}
                </div>
                <div aria-hidden={!showPromo} className={cx({"promoDescription": true, "show": showPromo})} dangerouslySetInnerHTML={{ __html: props.promoDescription }} />
            </div>
            } */}
      {!disableEcommerce && (
        <>
          {!props.price || props.price === 0 ? (
            <div className="row pdp-annsacks-not-have-price">
              {props.customLabel && props.customLabel.zeroPrice
                ? props.customLabel.zeroPrice
                : props.staticTexts.priceNotDefinedTxt}
            </div>
          ) : showBuyBoxArea ? (
            props.isDiscontinued ? (
              props.isColorDiscontinued ? (
                <div className="product-details__available-color-section">
                  {/* <Button className="product-details__available-color-btn"
                        onClick={() => props.onAvailableColor()}
                        flexible={true}
                        label={props.staticTexts.availColorCTA}
                        size="large" type="primary"></Button> */}
                </div>
              ) : null
            ) : !(productStock > 0) ? (
              props.specialOrder === "Yes" ||
              (props.backOrder === "Yes" && props.templateType !== "samples") ||
              props.isParts ? (
                <>
                  {stockAlert.show && (
                    <div className="product-details__stock-alert">
                      <img src={infoIcon} />
                      <span>{stockAlert.mssg}</span>
                    </div>
                  )}
                  <div className="row quantity-selector-block">
                    {showQuantitySelector && (
                      <div className="col-3 col-lg-2 col-md-3 quantity-selector-container">
                        <QuantitySelector
                          value={quantity}
                          onChange={handleChange}
                          loading={loading}
                        />
                      </div>
                    )}
                    <div
                      className={
                        showQuantitySelector
                          ? "col-9 col-lg-10 col-md-9 add-to-cart-container"
                          : "col-12 add-to-cart-container add-to-cart-button-main-container"
                      }
                    >
                      <Button
                        role="button"
                        className={`${
                          props.templateType === "tile" ||
                          props.templateType === "trim"
                            ? "add-to-cart__main-button disabled"
                            : "add-to-cart__main-button"
                        }`}
                        label={props.staticTexts.addToCart}
                        onClick={debounce(handleAddToCart, 500)}
                        size="large"
                        type="primary"
                        flexible={true}
                        disabled={
                          loading ||
                          !quantity ||
                          parseInt(quantity) < 1 ||
                          !props.price
                        }
                        tabIndex={!quantity ? -1 : 0}
                        templateType={props.templateType}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="out-of-stock-container">
                  <span className="out-of-stock">
                    {props.staticTexts.outOfStockMsg}
                  </span>
                  <span className="out-of-stock-description">
                    {props.staticTexts.notifyEmailTxt}
                  </span>
                  <div className="email-address-field">
                    <Input
                      id="email"
                      className="email_input "
                      type="text"
                      maxLength={60}
                      value={email}
                      label={props.staticTexts.emailAddress}
                      onChange={e => handleEmailChange(e.target.value)}
                      showError={emailError.show}
                      errorMessage={emailError.message}
                    />
                  </div>
                  {showWelcome && (
                    <div className="out-of-stock-success-popup">
                      <Modal
                        showModal={showWelcome}
                        onModalClose={() => setShowWelcome(false)}
                      >
                        <SuccessPopup
                          staticTexts={props.staticTexts}
                          closePopup={() => setShowWelcome(false)}
                        ></SuccessPopup>
                      </Modal>
                    </div>
                  )}
                  <div className="notify-btn">
                    <Button
                      label={props.staticTexts.notifyMailCTA}
                      onClick={handleNotification}
                      size="large"
                      type="primary"
                      flexible={true}
                      disabled={!enableNotify}
                    />
                  </div>
                </div>
              )
            ) : (
              <>
                {props.onChangeStockAlert.show ? (
                  <div className="product-details__stock-alert">
                    <img src={infoIcon} />
                    <span>{props.onChangeStockAlert.mssg}</span>
                  </div>
                ) : (
                  stockAlert.show && (
                    <div className="product-details__stock-alert">
                      <img src={infoIcon} />
                      <span>{stockAlert.mssg}</span>
                    </div>
                  )
                )}
                <div className="row quantity-selector-block">
                  {showQuantitySelector && (
                    <div className="col-3 col-lg-2 col-md-3 quantity-selector-container">
                      <QuantitySelector
                        value={quantity}
                        onChange={handleChange}
                        loading={loading}
                        productStock={productStock}
                      />
                    </div>
                  )}

                  <div
                    className={
                      showQuantitySelector
                        ? "col-9 col-lg-10 col-md-9 add-to-cart-container"
                        : "col-12 add-to-cart-container add-to-cart-button-main-container"
                    }
                  >
                    {props.SKUInventoryStatus === madeToOrderStatus ? (
                      <>
                        <Button
                          role="button"
                          label={
                            props.authorData.contactustoorder
                              ? props.authorData.contactustoorder
                              : props.staticTexts.contactUsOrder
                          }
                          size="large"
                          type="primary"
                          flexible={true}
                          tabIndex={
                            productStock < quantity || !quantity ? -1 : 0
                          }
                          className={`${
                            props.templateType === "tile" ||
                            props.templateType === "trim"
                              ? "add-to-cart__main-button disabled"
                              : "add-to-cart__main-button"
                          }`}
                          onClick={() => handleMadetoOrder()}
                          disabled={
                            productStock < quantity ||
                            !quantity ||
                            parseInt(quantity) < 1
                          }
                        />
                        <MadetoOrder
                          showModal={showModal}
                          closeModal={closeModal}
                          {...props}
                          activeTab={activeTab}
                          setActiveTab={setActiveTab}
                        />
                      </>
                    ) : (
                      <>
                        <Button
                          role="button"
                          className={`${
                            props.templateType === "tile" ||
                            props.templateType === "trim"
                              ? "add-to-cart__main-button disabled"
                              : "add-to-cart__main-button"
                          }`}
                          label={props.staticTexts.addToCart}
                          tabIndex={
                            productStock < quantity || !quantity ? -1 : 0
                          }
                          onClick={debounce(handleAddToCart, 500)}
                          size="large"
                          type="primary"
                          flexible={true}
                          disabled={
                            productStock < quantity ||
                            !quantity ||
                            parseInt(quantity) < 1
                          }
                          templateType={props.templateType}
                        />
                      </>
                    )}
                  </div>
                </div>
              </>
            )
          ) : null}
        </>
      )}
      {/* enable replaceAddToCart */}
      {replaceAddToCart && !props.isDiscontinued && (
        <div className="row quantity-selector-block">
          <div className="col-12 add-to-cart-container add-to-cart-button-main-container">
            <Button
              id="ecommerce-cart-btn"
              className="ecommerce-cart__main-button"
              label={replaceAddToCartCtaLabel}
              size="large"
              type="primary"
              flexible={true}
              tag="anchor"
              to={
                replaceAddToCartCtaLink
                  ? getShortenedUrlwithGeneral(
                      replaceAddToCartCtaLink,
                      props.general
                    )
                  : "javascript:void(0)"
              }
              target={linkNewTab === "true" ? "_blank" : "_self"}
              onClick={() => {
                handleAtcDatalayer({
                  title: replaceAddToCartCtaLabel?.toLowerCase(),
                  link: `${
                    window.location.origin +
                    getShortenedUrlwithGeneral(
                      replaceAddToCartCtaLink,
                      props.general
                    )
                  }`,
                })
              }}
            />
          </div>
        </div>
      )}

      <div
        className={`bottom-section row ${
          props.isDiscontinued ? "bottom-section-print-block" : ""
        }`}
      >
        {!props.isDiscontinued && (
          <React.Fragment>
            <div className="add-to-compare">
              <Checkbox
                value={props.staticTexts.compareTxt}
                checked={isChecked}
                onChange={e => handleCompare(e)}
                disabled={props.isEnable}
              />
              {errorMessage !== "" && <ToolTip message={errorMessage} />}
            </div>
            <div
              className={cx({
                favorites: true,
                "favorites--discontinued": enableFav,
              })}
              onClick={e => handleFavorite(e)}
              role="button"
              tabIndex="0"
              aria-label={props.staticTexts.favTxt}
            >
              <img
                src={props.isFavorite ? filledWishIcon?.src : wishIcon?.src}
                alt={props.staticTexts.favTxt}
              />
              <span className="text">{props.staticTexts.favTxt} </span>
            </div>
          </React.Fragment>
        )}
        <div className="print-share-container">
          <div className="print-and-share">
            <span className="print-button" tabIndex="0" onClick={() => printPDP()}>
              <img src={printIcon?.src} alt={props.staticTexts.print} />
              {props.staticTexts.print}
            </span>
            <span
            tabIndex="0"
              className="share-button"
              onClick={() => setShowSocialLink(!showSocialLink)}
              ref={shareImg}
            >
              <img src={shareIcon?.src} alt={props.staticTexts.share} />
              {props.staticTexts.share}{" "}
            </span>
          </div>
          <SocialShare
            showModal={showSocialLink}
            id="kf-product-detail"
            onClose={() => {
              setShowSocialLink(false)
              shareImg?.current.focus()
            }}
            shareBtn={shareImg}
            url={`${window.location.origin}${router?.asPath}`}
            asset={
              props.imagesData[0] ? { url: props.imagesData[0]?.url } : false
            }
            analyticData={{
              eventAction: "pdp:product details:share",
              eventName: "pdp:product details:share",
              internalLinkPosition: "pdp",
              internalLinkType: "pdp:share",
              internalLinkZoneName: "pdp:product details",
              custom: JSON.stringify({
                productInfo:
                  window?.adobeDataLayer?.getState("page")?.productInfo,
              }),
            }}
          />
        </div>
      </div>
    </div>
  )
}

AddToCart.propTypes = {
  staticTexts: PropTypes.object,
}

export default AddToCart

const SuccessPopup = props => {
  return (
    <div className="success-popup">
      <span className="success-popup__title">
        {props.staticTexts.successTxt}
      </span>
      <span className="success-popup__message">
        {props.staticTexts.successMsg}
      </span>
      <div className="success-popup__done-cta">
        <Button
          className="doneCTA"
          label={props.staticTexts.doneCTA}
          size="small"
          type="ghost"
          onClick={props.closePopup}
        />
      </div>
    </div>
  )
}
